import { useState } from "react";
import { Card, CardContent } from "../../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { TopResourceActivityProps } from "./interfaces/interface";
import { Skeleton } from "../interfaces/CommonInterfaces";
import { cn } from "../../../utils/utils";

export default function TopResourceActivity({
  fromdetails = false,
  topUsers,
  topResources,
  topComputers,
  loading
}: TopResourceActivityProps) {
  const [activeSubTab, setActiveSubTab] = useState("users");

  return (
    <Card className="flex-shrink-0">
      <CardContent className="pt-6">
        <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
          <TabsList
            className={cn(
              "grid w-full",
              !fromdetails ? "grid-cols-3" : "grid-cols-2"
            )}
          >
            {!fromdetails && <TabsTrigger
              value="computers"
              data-testid="tab-top-users"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:font-medium"
            >
              Top Computers
            </TabsTrigger>}
            <TabsTrigger
              value="users"
              data-testid="tab-top-users"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:font-medium"
            >
              Top Users
            </TabsTrigger>
            <TabsTrigger
              value="resources"
              data-testid="tab-top-resources"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:font-medium"
            >
              Top Resources
            </TabsTrigger>
          </TabsList>

          {/* Top computers Tab */}
          <TabsContent value="computers" className="mt-4">
            <div className="space-y-3">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center justify-between gap-4 py-1.5">
                    <div className="flex items-start flex-1 min-w-0 gap-2">
                      <Skeleton className="w-4 h-4 rounded" />
                      <Skeleton className="w-32 h-4" />
                    </div>
                    <div className="flex items-center gap-2 min-w-[120px]">
                      <Skeleton className="h-2 w-[100px] rounded-full" />
                      <Skeleton className="w-8 h-4" />
                    </div>
                  </div>
                ))
              ) : topComputers && topComputers.length > 0 ? (
                topComputers.map((user, index) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between gap-4 py-1.5 cursor-pointer"
                    data-testid={`list-user-${user.id}`}
                    title={`Count: ${user.count}`}
                  >
                    <div className="flex items-start flex-1 min-w-0 gap-2">
                      <span className="flex-shrink-0 text-sm font-medium">{index + 1}.</span>
                      <span className="text-sm break-words text-foreground">{user.name}</span>
                    </div>
                    <div className="flex items-center gap-2 min-w-[120px]">
                      <div className="w-full max-w-[100px] h-2 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${user.percent}%` }}
                        />
                      </div>
                      <span className="w-12 text-xs text-right text-muted-foreground">{user.percent || 0}%</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-6 text-sm text-center text-muted-foreground">
                  No data found.
                </div>
              )}
            </div>
          </TabsContent>

          {/* Top Users Tab */}
          <TabsContent value="users" className="mt-4">
            <div className="space-y-3">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center justify-between gap-4 py-1.5">
                    <div className="flex items-start flex-1 min-w-0 gap-2">
                      <Skeleton className="w-4 h-4 rounded" />
                      <Skeleton className="w-32 h-4" />
                    </div>
                    <div className="flex items-center gap-2 min-w-[120px]">
                      <Skeleton className="h-2 w-[100px] rounded-full" />
                      <Skeleton className="w-8 h-4" />
                    </div>
                  </div>
                ))
              ) : topUsers && topUsers.length > 0 ? (
                topUsers.map((user, index) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between gap-4 py-1.5 cursor-pointer"
                    data-testid={`list-user-${user.id}`}
                    title={`Count: ${user.count}`}
                  >
                    <div className="flex items-start flex-1 min-w-0 gap-2">
                      <span className="flex-shrink-0 text-sm font-medium">{index + 1}.</span>
                      <span className="text-sm break-words text-foreground">{user.user}</span>
                    </div>
                    <div className="flex items-center gap-2 min-w-[120px]">
                      <div className="w-full max-w-[100px] h-2 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${user.percent}%` }}
                        />
                      </div>
                      <span className="w-12 text-xs text-right text-muted-foreground">{user.percent || 0}%</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-6 text-sm text-center text-muted-foreground">
                  No data found.
                </div>
              )}
            </div>
          </TabsContent>

          {/* Top Resources Tab */}
          <TabsContent value="resources" className="mt-4">
            <div className="space-y-3">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center justify-between gap-4 py-1.5">
                    <div className="flex items-start flex-1 min-w-0 gap-2">
                      <Skeleton className="w-4 h-4 rounded" />
                      <Skeleton className="w-32 h-4" />
                    </div>
                    <div className="flex items-center gap-2 min-w-[120px]">
                      <Skeleton className="h-2 w-[100px] rounded-full" />
                      <Skeleton className="w-8 h-4" />
                    </div>
                  </div>
                ))
              ) : topResources && topResources.length > 0 ? (
                topResources.map((resource, index) => (
                  <div
                    key={resource.id}
                    className="flex items-center justify-between gap-4 py-1.5 cursor-pointer"
                    data-testid={`list-resource-${resource.id}`}
                    title={`Count: ${resource.count}`}
                  >
                    <div className="flex items-start flex-1 min-w-0 gap-2">
                      <span className="flex-shrink-0 text-sm font-medium">{index + 1}.</span>
                      <span className="text-sm break-words text-foreground">{resource.resource}</span>
                    </div>
                    <div className="flex items-center gap-2 min-w-[120px]">
                      <div className="w-full max-w-[100px] h-2 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${resource.percent}%` }}
                        />
                      </div>
                      <span className="w-12 text-xs text-right text-muted-foreground">{resource.percent || 0}%</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-6 text-sm text-center text-muted-foreground">
                  No data found.
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
